import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users2, PhoneCall, Send, Calendar, Award, Percent, ArrowUpRight, ArrowDownRight, Minus, Sparkles, TrendingUp } from "lucide-react";
import api from "@/lib/api";

const CLASS_COLOR = {
  excellent: "text-[hsl(var(--success))]",
  target:    "text-[hsl(var(--success))]",
  attention: "text-[hsl(var(--warning))]",
  critical:  "text-[hsl(var(--danger))]",
  unknown:   "text-muted-foreground",
};

const DIRECTION_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus };
const DIRECTION_COLOR = { up: "text-[hsl(var(--success))]", down: "text-[hsl(var(--danger))]", flat: "text-muted-foreground" };

function DeltaBadge({ delta }) {
  if (!delta || delta.pct === null || delta.pct === undefined) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  const Icon = DIRECTION_ICON[delta.direction] || Minus;
  const color = DIRECTION_COLOR[delta.direction];
  const abs = Math.abs(delta.pct).toFixed(1);
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${color}`}>
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {abs}%
    </span>
  );
}

function KpiChip({ label, value, delta, icon: Icon, colorClass, testid, suffix }) {
  // 04/08/2026 — Refactored from a side-by-side icon+text layout to a
  // stacked one: labels like "Leads recebidos" or "SLA ≤15min" were
  // being truncated to "LEADS…" when 6 chips shared a row. The label
  // now spans the full chip width, and the icon sits inline next to
  // the value below (still visually anchored, no truncation).
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 rounded-md bg-card border border-border flex items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5 text-foreground" strokeWidth={1.5} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold leading-tight">
          {label}
        </div>
      </div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <div className={`font-display text-[26px] leading-none tracking-tight ${colorClass || "text-foreground"}`}>
          {value}{suffix || ""}
        </div>
        <DeltaBadge delta={delta} />
      </div>
    </div>
  );
}

/** Compact Performance-do-Atendimento strip for the main /dashboard. */
export default function PerformanceCompactWidget({ period = "today" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/performance/summary?period=${period}&include_breakdown=false`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  if (loading || !data) return null;

  const c = data.counts || {};
  const conv = data.conversions || {};
  const cls = data.classifications || {};
  const sla = data.sla || {};
  const deltas = data.deltas || {};
  const convDeltas = deltas.conversions || {};

  const periodLabelMap = {
    today: "HOJE", yesterday: "ONTEM", week: "SEMANA", month: "MÊS CORRENTE", all: "TOTAL",
  };

  return (
    <div className="bg-card rounded-2xl p-6 lg:p-7 mb-8 text-foreground relative overflow-hidden border border-border shadow-sm" data-testid="performance-compact">
      {/* soft accent wash — very subtle so it stays light */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 900px 260px at 0% 0%, rgba(29,78,216,0.06), transparent 60%)",
      }} />
      <div className="relative">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--info-bg))] border border-primary/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-primary font-heading font-semibold">
                Performance do Atendimento · {periodLabelMap[period] || period.toUpperCase()}
              </div>
              <h3 className="font-display text-xl lg:text-2xl text-foreground">Visão executiva</h3>
            </div>
          </div>
          <Link
            to="/performance"
            className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg bg-foreground hover:bg-slate-800 text-background font-heading font-semibold tracking-tight transition-colors"
            data-testid="perf-view-detail-btn"
          >
            Ver performance detalhada <TrendingUp className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5 items-center">
          <div className="bg-muted/40 rounded-xl p-3 border border-border">
            <KpiChip
              label="Recebidos"
              value={c.leads_received || 0}
              delta={deltas.leads_received}
              icon={Users2}
              colorClass="text-foreground"
              testid="perf-chip-leads"
            />
          </div>
          <div className="bg-muted/40 rounded-xl p-3 border border-border">
            <KpiChip
              label="Contactados"
              value={c.leads_contacted || 0}
              delta={deltas.leads_contacted}
              icon={PhoneCall}
              colorClass="text-foreground"
              testid="perf-chip-contacted"
            />
          </div>
          <div className="bg-muted/40 rounded-xl p-3 border border-border">
            <KpiChip
              label={`SLA ≤${sla.sla_limit_minutes || 15}min`}
              value={sla.compliance_pct != null ? sla.compliance_pct.toFixed(0) : "—"}
              suffix={sla.compliance_pct != null ? "%" : ""}
              delta={deltas.sla_compliance}
              icon={Percent}
              colorClass={CLASS_COLOR[cls.sla_compliance] || "text-foreground"}
              testid="perf-chip-sla"
            />
          </div>
          <div className="bg-muted/40 rounded-xl p-3 border border-border">
            <KpiChip
              label="Estimativas"
              value={c.estimates_sent || 0}
              delta={deltas.estimates_sent}
              icon={Send}
              colorClass="text-foreground"
              testid="perf-chip-estimates"
            />
          </div>
          <div className="bg-muted/40 rounded-xl p-3 border border-border">
            <KpiChip
              label="RT agendadas"
              value={c.rt_scheduled || 0}
              delta={deltas.rt_scheduled}
              icon={Calendar}
              colorClass="text-foreground"
              testid="perf-chip-rt"
            />
          </div>
          <div className="bg-muted/40 rounded-xl p-3 border border-border">
            <KpiChip
              label="Negócios"
              value={c.deals_closed || 0}
              delta={deltas.deals_closed}
              icon={Award}
              colorClass="text-[hsl(var(--success))]"
              testid="perf-chip-deals"
            />
          </div>
        </div>

        {/* Conversion summary strip */}
        <div className="mt-5 pt-5 border-t border-border grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading font-semibold">Est → RT</div>
            <div className="flex items-baseline gap-2">
              <span className={`font-display text-xl ${CLASS_COLOR[cls.estimate_to_rt] || "text-foreground"}`}>
                {conv.estimate_to_rt?.toFixed(1) ?? "0.0"}%
              </span>
              <DeltaBadge delta={convDeltas.estimate_to_rt} />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading font-semibold">Proposta → Negócio</div>
            <div className="flex items-baseline gap-2">
              <span className={`font-display text-xl ${CLASS_COLOR[cls.proposal_to_deal] || "text-foreground"}`}>
                {conv.proposal_to_deal?.toFixed(1) ?? "0.0"}%
              </span>
              <DeltaBadge delta={convDeltas.proposal_to_deal} />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading font-semibold">Lead → Negócio</div>
            <div className="flex items-baseline gap-2">
              <span className={`font-display text-xl ${CLASS_COLOR[cls.lead_to_deal] || "text-foreground"}`}>
                {conv.lead_to_deal?.toFixed(1) ?? "0.0"}%
              </span>
              <DeltaBadge delta={convDeltas.lead_to_deal} />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading font-semibold">Sem tratamento</div>
            <div className="flex items-baseline gap-2">
              <span className={`font-display text-xl ${(sla.leads_untouched || 0) > 0 ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--success))]"}`}>
                {sla.leads_untouched || 0}
              </span>
              <span className="text-[10px] text-muted-foreground">leads</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
